import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  CheckCircle,
  AlertTriangle,
  Info,
  Clock,
  CheckCheck,
  Trash2,
  MessageSquare,
  AlertOctagon,
  BellOff
} from 'lucide-react';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'warning' | 'info';
  timestamp: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    title: 'Task Completed',
    message: 'HVAC maintenance in Building A has been completed successfully',
    type: 'success',
    timestamp: '2024-01-20T14:30:00Z',
    read: false,
  },
  {
    id: '2',
    title: 'Subscription Renewal',
    message: 'Your subscription will renew in 7 days. Please ensure your payment method is up to date.',
    type: 'warning',
    timestamp: '2024-01-20T12:00:00Z',
    read: false,
  },
  {
    id: '3',
    title: 'New Message',
    message: 'You have a new message from John regarding Unit 304 maintenance request',
    type: 'info',
    timestamp: '2024-01-20T10:15:00Z',
    read: true,
  },
];

export function NotificationsPage() {
  const [notifications, setNotifications] = useState(mockNotifications);
  const { toast } = useToast();

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAllRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    toast({
      title: 'Success',
      description: 'All notifications marked as read',
    });
  };

  const handleClearAll = () => {
    setNotifications([]);
    toast({
      title: 'Success',
      description: 'All notifications cleared',
    });
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default:
        return <Info className="h-5 w-5 text-primary" />;
    }
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-12 flex flex-col items-center justify-center text-center"
    >
      <div className="bg-primary/10 h-16 w-16 rounded-full flex items-center justify-center mb-4">
        <BellOff className="h-8 w-8 text-primary" />
      </div>
      <h3 className="text-lg font-medium">No notifications</h3>
      <p className="text-muted-foreground mt-1">
        You're all caught up! New notifications will appear here.
      </p>
    </motion.div>
  );

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">Notifications</h1>
            <p className="text-muted-foreground">Stay updated with the latest activities</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              {unreadCount} unread
            </Badge>
          )}
          <div className="flex gap-2">
            <Button variant="glass" size="sm" onClick={handleMarkAllRead} className="whitespace-nowrap">
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark All Read
            </Button>
            <Button variant="glass" size="sm" onClick={handleClearAll} className="whitespace-nowrap">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear All
            </Button>
          </div>
        </div>
      </motion.div>

      <Tabs defaultValue="all" className="space-y-6">
        <TabsList className="bg-background/50 backdrop-blur-sm w-full md:w-auto">
          <TabsTrigger value="all" className="data-[state=active]:bg-primary/10">
            All
          </TabsTrigger>
          <TabsTrigger value="unread" className="data-[state=active]:bg-primary/10">
            Unread
          </TabsTrigger>
          <TabsTrigger value="tasks" className="data-[state=active]:bg-primary/10">
            Tasks
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-primary/10">
            System
          </TabsTrigger>
        </TabsList>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <TabsContent value="all">
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>All Notifications</CardTitle>
                <CardDescription>All your recent notifications across the system</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[550px] pr-4">
                  <AnimatePresence>
                    {notifications.length > 0 ? (
                      <div className="space-y-4">
                        {notifications.map((notification, index) => (
                          <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className={`flex items-start gap-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm transition-all hover:bg-background/70 ${
                              !notification.read ? 'border-l-4 border-l-primary shadow-sm' : ''
                            }`}
                          >
                            <div className={`rounded-full p-3 ${
                              notification.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20' :
                              notification.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20' :
                              'bg-primary/10'
                            }`}>
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{notification.title}</p>
                                {!notification.read && (
                                  <Badge variant="default" className="bg-primary/80">New</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {notification.message}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {new Date(notification.timestamp).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState />
                    )}
                  </AnimatePresence>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="unread">
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Unread Notifications</CardTitle>
                <CardDescription>Notifications you haven't seen yet</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[550px] pr-4">
                  <AnimatePresence>
                    {notifications.filter(n => !n.read).length > 0 ? (
                      <div className="space-y-4">
                        {notifications
                          .filter(n => !n.read)
                          .map((notification, index) => (
                            <motion.div
                              key={notification.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              transition={{ delay: index * 0.05 }}
                              className="flex items-start gap-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm border-l-4 border-l-primary shadow-sm transition-all hover:bg-background/70"
                            >
                              <div className={`rounded-full p-3 ${
                                notification.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20' :
                                notification.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20' :
                                'bg-primary/10'
                              }`}>
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium">{notification.title}</p>
                                  <Badge variant="default" className="bg-primary/80">New</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  <span>
                                    {new Date(notification.timestamp).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </div>
                    ) : (
                      <EmptyState />
                    )}
                  </AnimatePresence>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Task Notifications</CardTitle>
                <CardDescription>Updates related to your tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <EmptyState />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="system">
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>System Notifications</CardTitle>
                <CardDescription>Updates about the system and your account</CardDescription>
              </CardHeader>
              <CardContent>
                <EmptyState />
              </CardContent>
            </Card>
          </TabsContent>
        </motion.div>
      </Tabs>
    </div>
  );
}